import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * A dropdown menu that cannot be clipped by an ancestor's overflow.
 *
 * ## The bug this exists to fix
 *
 * The invoice table's row menu was cut off mid-item — "Open Details" was readable and
 * everything below it was sliced away. The menu was `absolute` inside a `relative` cell
 * with `z-50`, which looks correct and is not: **z-index cannot rescue an element from an
 * ancestor's overflow.** Clipping happens first, and stacking order never enters into it.
 *
 * The table wrapper was written as `overflow-x-auto overflow-y-visible`, which reads like
 * "scroll sideways, let menus escape downwards". That combination does not exist. Per the
 * CSS overflow spec, when one axis is anything other than `visible`, a `visible` on the
 * other axis **computes to `auto`** — so the wrapper clipped vertically, exactly the
 * thing the class was written to prevent. It is a silent no-op that looks like a fix.
 *
 * ## Why a portal rather than more CSS
 *
 * There is no CSS that gets an absolutely-positioned child out of a scrolling ancestor;
 * the only real options are to stop the ancestor scrolling (which breaks wide tables on
 * small screens) or to take the menu out of that subtree entirely. This renders into
 * `document.body` and positions with `fixed` coordinates from the trigger's rect, so no
 * ancestor can clip it regardless of how the page is laid out later.
 *
 * `createPortal` is already the established pattern in this codebase for escaping
 * containers — see `AccountingSoftwarePage` and `XeroConfigPage`.
 *
 * ## Positioning
 *
 * Measured after mount in a layout effect, so the flip decision uses the menu's real
 * height rather than a guess. The invoice page previously hardcoded "flip up if less than
 * 200px below", which is wrong for both a two-item menu and a seven-item one.
 *
 * Because the position is `fixed` and derived from a rect taken at open time, the menu is
 * closed on scroll and resize rather than tracked — a menu that silently detaches from
 * its row is worse than one that closes.
 *
 * @param {React.ReactNode} trigger  rendered as the button's content
 * @param {string}   [align]         'right' (default) or 'left', relative to the trigger
 * @param {number}   [width]         menu width in px, default 208 (Tailwind w-52)
 * @param {string}   [triggerClassName]
 * @param {string}   [triggerTitle]  accessible label for the trigger
 * @param {Function} children        ({ close }) => menu items
 */
export default function OverflowSafeMenu({
    trigger,
    align = 'right',
    width = 208,
    triggerClassName = 'p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors',
    triggerTitle = 'More actions',
    children,
}) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const close = useCallback(() => setOpen(false), []);

    // Take the trigger's position at open time. Stored rather than read during render so
    // the layout effect below can flip it once the menu's real height is known.
    const openMenu = useCallback((event) => {
        event.stopPropagation();
        if (open) { close(); return; }
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setCoords({ top: rect.bottom, bottom: rect.top, left: rect.left, right: rect.right });
        setOpen(true);
    }, [open, close]);

    // Flip above the trigger when the menu would run off the bottom of the viewport.
    // useLayoutEffect, not useEffect: this runs before paint, so the menu never appears
    // in the wrong place for a frame and jumps.
    useLayoutEffect(() => {
        if (!open || !coords || !menuRef.current) return;
        const height = menuRef.current.offsetHeight;
        const fitsBelow = coords.top + height <= window.innerHeight - 8;
        if (!fitsBelow && coords.bottom - height >= 8) {
            setCoords(c => (c.flipped ? c : { ...c, flipped: true }));
        }
    }, [open, coords]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (e) => { if (e.key === 'Escape') close(); };
        // Capture phase: a scroll inside the table does not bubble to window, and this
        // menu's whole purpose is living outside that scrolling container.
        const onScroll = () => close();
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', close);
        };
    }, [open, close]);

    const style = coords
        ? {
            position: 'fixed',
            width,
            ...(coords.flipped
                ? { bottom: Math.max(8, window.innerHeight - coords.bottom + 4) }
                : { top: coords.top + 4 }),
            ...(align === 'right'
                ? { left: Math.max(8, coords.right - width) }
                : { left: Math.max(8, coords.left) }),
        }
        : {};

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={openMenu}
                className={triggerClassName}
                title={triggerTitle}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {trigger}
            </button>

            {open && ReactDOM.createPortal(
                <>
                    {/* Click-away layer. Below the menu, above everything else. */}
                    <div
                        className="fixed inset-0 z-[900]"
                        data-testid="menu-backdrop"
                        onClick={(e) => { e.stopPropagation(); close(); }}
                    />
                    <div
                        ref={menuRef}
                        role="menu"
                        style={style}
                        className="z-[901] bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {typeof children === 'function' ? children({ close }) : children}
                    </div>
                </>,
                document.body,
            )}
        </>
    );
}

/** One item in an {@link OverflowSafeMenu}. Exists so callers stop repeating the classes. */
export function MenuItem({ icon, children, onClick, danger = false }) {
    return (
        <button
            type="button"
            role="menuitem"
            onClick={onClick}
            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
            {icon}
            {children}
        </button>
    );
}
